import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Grid, Typography, IconButton, Menu, MenuItem, Box } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar
} from "recharts";
import SkeletonTotalGrowthBarChart from "./ui-component/cards/Skeleton/TotalGrowthBarChart";

interface DonutChartProps {
    isLoading: boolean;
    data: number[];
    type: "pie" | "donut" | "polarArea" | "radialBar";
    labels: Array<string>;
    legendPosition?: "bottom" | "right";
    compact?: boolean;
    colorsByLabel?: Record<string, string>;
}

const typographyPrometo = { fontFamily: "Prometo, sans-serif" };

const CustomTooltip = ({ active, payload, theme }: any) => {
  if (active && payload && payload.length) {
    return (
      <div 
        style={{ 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
          padding: '12px', 
          borderRadius: '8px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.palette.divider}`,
          ...typographyPrometo
        }}
      >
        <Typography variant="body2" style={{ ...typographyPrometo, fontWeight: 500, color: theme.palette.text.primary }}>
          {payload[0].name}: {payload[0].value}
        </Typography>
      </div>
    );
  }
  return null;
};

const TotalDonutChart = ({ isLoading, data, type, labels, legendPosition = "bottom", compact = false, colorsByLabel }: DonutChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Paleta redefinida atrelada ao tema local (Soft Palette dinâmica)
  const COLORS = useMemo(() => [
    alpha(theme.palette.primary.main || "#673ab7", 0.55),
    alpha(theme.palette.secondary.main || "#2196f3", 0.55),
    alpha(theme.palette.success.main || "#00e676", 0.55),
    alpha(theme.palette.warning.main || "#ff9800", 0.55),
    alpha(theme.palette.info.main || "#00bcd4", 0.55),
    alpha(theme.palette.error.main || "#f44336", 0.55),
    alpha(theme.palette.primary.light || "#ede7f6", 0.7)
  ], [theme]);

  const baseData = useMemo(() => {
    return (data || []).map((val, idx) => {
      const labelVal = labels?.[idx];
      const validName = (labelVal && typeof labelVal === 'string' && labelVal.trim() !== '') ? labelVal : "Não Informado";
      return {
        name: validName,
        value: val,
        fill: colorsByLabel?.[validName] ?? COLORS[idx % COLORS.length]
      };
    });
  }, [data, labels, COLORS, colorsByLabel]);

  const visibleData = useMemo(() => {
    return baseData.filter((item) => !hiddenSeries.includes(item.name));
  }, [baseData, hiddenSeries]);

  const isEmpty = useMemo(() => {
    return !baseData.some((d) => d.value > 0);
  }, [baseData]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => setChartWidth(entry.contentRect.width));
    observer.observe(chart);
    return () => observer.disconnect();
  }, [isLoading, isEmpty]);

  const showLegendOnRight = legendPosition === "right" && chartWidth >= 520;

  const handleLegendClick = (name: string) => {
    if (hiddenSeries.includes(name)) {
      setHiddenSeries(hiddenSeries.filter((k) => k !== name));
    } else {
      setHiddenSeries([...hiddenSeries, name]);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const exportCSV = () => {
    const csvContent = [
      "Categoria,Valor",
      ...baseData.map(d => `${d.name},${d.value}`)
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chart_distribution_export.csv";
    link.click();
    handleMenuClose();
  };

  const exportSVG = () => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector(".recharts-wrapper svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart_distribution_export.svg";
    link.click();
    URL.revokeObjectURL(url);
    handleMenuClose();
  };

  const exportPNG = () => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector(".recharts-wrapper svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      if (ctx) {
        ctx.fillStyle = theme.palette.background.paper || "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const link = document.createElement("a");
        link.download = "chart_distribution_export.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    handleMenuClose();
  };

  if (isLoading) {
    return <SkeletonTotalGrowthBarChart />;
  }

  if (isEmpty || !data || data.length === 0) {
    return (
      <Grid className="dashboard-chart-surface tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" container justifyContent="center" alignItems="center" style={{ height: compact ? 300 : 350, background: "#f9fafb", borderRadius: 12, boxShadow: "none" }}>
        <Typography variant="h5" color="textSecondary" style={typographyPrometo}>
          Nenhum dado disponível.
        </Typography>
      </Grid>
    );
  }

  const isPieOrDonut = type === "pie" || type === "donut" || type === "polarArea";
  
  const renderInteractiveLegendRight = () => (
    <Box display="flex" flexDirection="column" gap={1.5} pl={2} pr={1} pb={2} sx={{ maxHeight: 280, overflowY: "auto", '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' } }}>
      {baseData.map((item, index) => {
        const isHidden = hiddenSeries.includes(item.name);
        return (
          <Box 
            key={`item-${index}`} 
            onClick={() => handleLegendClick(item.name)}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', opacity: isHidden ? 0.5 : 1, transition: '0.2s', flexShrink: 0 }}
          >
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: isHidden ? '#ccc' : item.fill, mr: 1, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ ...typographyPrometo, color: theme.palette.text.secondary, textDecoration: isHidden ? 'line-through' : 'none', fontSize: 13, wordBreak: 'break-word', lineHeight: 1.2 }}>
              {item.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderInteractiveLegendBottom = () => (
    <Box 
       sx={{ 
         display: 'flex',
         flexWrap: 'wrap',
         columnGap: compact ? 2 : 3,
         rowGap: 1,
         px: 2, 
         pb: 2, 
         width: "100%", 
         maxHeight: 82,
         justifyContent: 'center',
         overflowY: 'auto',
         '&::-webkit-scrollbar': { width: '6px' },
         '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' } 
       }}
    >
      {baseData.map((item, index) => {
        const isHidden = hiddenSeries.includes(item.name);
        return (
          <Box 
            key={`item-${index}`} 
            onClick={() => handleLegendClick(item.name)}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', opacity: isHidden ? 0.5 : 1, transition: '0.2s', flexShrink: 0 }}
          >
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: isHidden ? '#ccc' : item.fill, mr: 1, flexShrink: 0 }} />
            <Typography variant="body2" title={item.name} sx={{ ...typographyPrometo, color: theme.palette.text.secondary, textDecoration: isHidden ? 'line-through' : 'none', fontSize: compact ? 12 : 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: compact ? 120 : 160 }}>
              {item.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderClassicLabel = (props: any) => {
    if (props.percent < 0.04) return <g></g>;
    return (
      <text x={props.x} y={props.y} fill={props.fill} textAnchor={props.textAnchor} dominantBaseline="central" style={{ fontSize: 12, ...typographyPrometo }}>
        {props.value}
      </text>
    );
  };

  const renderClassicLabelLine = (props: any) => {
    if (props.percent < 0.04) return <g></g>;
    const { points } = props;
    if (!points || !Array.isArray(points)) return <g></g>;
    return (
      <polyline points={points.map((p: any) => `${p.x},${p.y}`).join(' ')} stroke={theme.palette.divider || "#ccc"} fill="none" />
    );
  };

  return (
    <div className="dashboard-chart-surface tw-relative tw-flex tw-w-full tw-min-w-0 tw-overflow-hidden tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" style={{ position: "relative", width: "100%", height: showLegendOnRight ? 350 : compact ? "clamp(290px, 36vw, 320px)" : "clamp(320px, 40vw, 380px)", background: "#f9fafb", borderRadius: 12, outline: "none", display: "flex", flexDirection: showLegendOnRight ? "row" : "column", alignItems: "stretch" }} ref={chartRef}>
      <style>{`.dashboard-chart-surface .recharts-wrapper, .dashboard-chart-surface .recharts-surface { outline: none !important; }`}</style>
      <IconButton className="!tw-bg-white/80 !tw-text-gtr-teal tw-shadow-sm hover:!tw-bg-white dark:!tw-bg-slate-800 dark:!tw-text-slate-100 dark:hover:!tw-bg-slate-700" onClick={handleMenuClick} size="small" style={{ position: "absolute", top: 8, right: 10, zIndex: 10 }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={exportSVG} style={typographyPrometo}>Download SVG</MenuItem>
        <MenuItem onClick={exportPNG} style={typographyPrometo}>Download PNG</MenuItem>
        <MenuItem onClick={exportCSV} style={typographyPrometo}>Download CSV</MenuItem>
      </Menu>
      
      <Box flex={1} minHeight={0} minWidth={0} width="100%" pt={showLegendOnRight ? 4 : 3}>
          <ResponsiveContainer>
            {isPieOrDonut ? (
               <PieChart>
                 <Pie
                   data={visibleData}
                   cx="50%" 
                   cy="50%"
                   innerRadius={type === "donut" ? "44%" : 0}
                   outerRadius={compact ? "68%" : "70%"}
                   paddingAngle={type === "donut" ? 3 : 0}
                   dataKey="value"
                   stroke="none"
                   animationDuration={800}
                   label={renderClassicLabel}
                   labelLine={renderClassicLabelLine}
                 >
                   {visibleData.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={entry.fill} />
                   ))}
                 </Pie>
                 <Tooltip content={<CustomTooltip theme={theme} />} />
               </PieChart>
            ) : (
                 <RadialBarChart 
                   cx="50%"  
                   cy="50%" 
                   innerRadius="20%" 
                   outerRadius="82%"
                   barSize={13}
                 data={visibleData}
               >
                 <RadialBar
                   background={{ fill: theme.palette.mode === 'dark' ? '#333' : '#eee' }}
                   dataKey="value"
                   cornerRadius={8}
                   animationDuration={800}
                   label={{ position: 'end', fill: theme.palette.text.primary, ...typographyPrometo, fontSize: 11 }}
                 />
                 <Tooltip content={<CustomTooltip theme={theme} />} />
               </RadialBarChart>
            )}
          </ResponsiveContainer>
      </Box>
      
      {showLegendOnRight ? (
        <Box sx={{ maxWidth: "45%", pr: 2, flexShrink: 0, display: "flex", alignItems: "center" }}>
          {renderInteractiveLegendRight()}
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          {renderInteractiveLegendBottom()}
        </Box>
      )}
    </div>
  );
};

export default TotalDonutChart;
